import React, { useContext, useState, useEffect } from 'react'
import { Button, Col, DatePicker, Drawer, Input, Select } from 'antd'
import { useQuery, useMutation } from '@apollo/react-hooks'
import _isEmpty from 'lodash/isEmpty'
import _get from 'lodash/get'
import moment from 'moment'

import MailList from './MailList'
import ComposeMail from './Compose'
import folders from './data/folders'
import MailDetail from './MailDetail'
import Spinner from 'components/loaders/Spinner'
import openNotification from 'utils/Notification'
import { COMPOSE_EMAIL } from './graphql/Mutations'
import CustomScrollbars from 'utils/CustomScrollbars'
import { FETCH_EMAIL_BY_ID, FETCH_EMAIL_DATA } from './graphql/ Queries'
import { AppContext } from 'AppContext'
import CustomMeta from 'components/CustomMeta'

const { Option } = Select
const PAGE_LIMIT = 10

export default function Mail(props) {
  const {
    match: {
      params: { email },
    },
  } = props
  const [currentMail, setCurrentMail] = useState(0)
  const [search, setSearch] = useState('')
  const [date, setDate] = useState('')
  const [composeMail, setComposeMail] = useState(false)
  const [drawerState, setDrawerState] = useState(false)
  const [selectedFolder, setSelectedFolder] = useState('INBOX')
  const [selectedAccount, setSelectedAccount] = useState('UPDATES')

  const [mailList, setMailList] = useState([])
  const [hasMore, setHasMore] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [offset, setOffset] = useState(0)

  const [sendMail, { loading: composeMailLoading }] = useMutation(COMPOSE_EMAIL)
  const {
    data: emailsData,
    loading: emailsLoading,
    error: emailsError,
    fetchMore,
  } = useQuery(FETCH_EMAIL_DATA, {
    variables: {
      emailFilter: email,
      fetchFrom: selectedFolder,
      account: selectedAccount,
      offset: 0,
      limit: PAGE_LIMIT,
    },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (emailsData && !emailsError) {
      const items = _get(emailsData, 'fetchEmail.result.data', [])
      const count = _get(emailsData, 'fetchEmail.result.count', 0)
      const serverHasMore = _get(emailsData, 'fetchEmail.result.hasMore')
      setMailList(items)
      setOffset(items.length)
      setHasMore(serverHasMore !== undefined ? serverHasMore : items.length < count)
    } else if (emailsError) {
      setMailList([])
      setOffset(0)
      setHasMore(false)
    }
  }, [emailsData, emailsError])

  const loadMoreEmails = async () => {
    if (loadingMore || !hasMore || emailsLoading) return
    setLoadingMore(true)
    try {
      const currentOffset = offset
      const { data: moreData } = await fetchMore({
        variables: {
          emailFilter: email,
          fetchFrom: selectedFolder,
          account: selectedAccount,
          offset: currentOffset,
          limit: PAGE_LIMIT,
        },
        updateQuery: (prev) => {
          return prev
        },
      })
      const newItems = _get(moreData, 'fetchEmail.result.data', [])
      const serverHasMore = _get(moreData, 'fetchEmail.result.hasMore')
      const totalCount = _get(moreData, 'fetchEmail.result.count', 0)

      if (newItems.length === 0) {
        setHasMore(false)
      } else {
        setMailList((prev) => {
          const existingUids = new Set(prev.map((m) => m.messageUid))
          const filteredNew = newItems.filter((m) => !existingUids.has(m.messageUid))
          return [...prev, ...filteredNew]
        })
        setOffset((prev) => prev + newItems.length)
        setHasMore(
          serverHasMore !== undefined
            ? serverHasMore
            : currentOffset + newItems.length < totalCount
        )
      }
    } catch (err) {
      console.error('Error loading more emails:', err)
    } finally {
      setLoadingMore(false)
    }
  }

  const {
    state: { isMobile },
  } = useContext(AppContext)

  const {
    data: detailEmailData,
    loading: detailEmailLoading,
    error: detailEmailError,
  } = useQuery(FETCH_EMAIL_BY_ID, {
    variables: {
      messageId: String(currentMail),
      fetchFrom: selectedFolder,
      account: selectedAccount,
    },
    fetchPolicy: 'network-only',
    skip: !currentMail || currentMail === 0 || currentMail === '0',
  })

  let detailEmail = null

  if (currentMail === 0) {
    detailEmail = null
  } else if (
    !detailEmailError &&
    _get(detailEmailData, 'fetchEmailById.result')
  ) {
    detailEmail = _get(detailEmailData, 'fetchEmailById.result')
  } else {
    detailEmail = 'None'
  }

  function MailSideBar() {
    return (
      <div className='gx-module-side'>
        <div className='gx-module-side-header'>
          <div className='gx-module-logo'>
            <i className='icon icon-chat gx-mr-4' />
            Mailbox
          </div>
        </div>

        <div className='gx-module-side-content'>
          <CustomScrollbars className='gx-module-side-scroll'>
            <div className='gx-module-add-task'>
              <Button
                type='primary'
                className='gx-btn-block'
                onClick={() => {
                  setComposeMail(true)
                }}
              >
                <i
                  className='icon icon-edit gx-mr-2'
                  style={{
                    fontSize: '20px',
                  }}
                />
                COMPOSE
              </Button>
            </div>

            <ul className='gx-module-nav'>{getNavFolders()}</ul>
          </CustomScrollbars>
        </div>
      </div>
    )
  }

  function getNavFolders() {
    return folders.map((folder, index) => (
      <li
        key={index}
        onClick={() => {
          setCurrentMail(0)
          setMailList([])
          setOffset(0)
          setHasMore(true)
          setSelectedFolder(folder.id)
        }}
      >
        <span
          className={`${
            selectedFolder === folder.id ? 'active gx-link' : 'gx-link'
          }`}
        >
          <i className={`icon icon-${folder.icon}`} />
          <span>{folder.title}</span>
        </span>
      </li>
    ))
  }

  function handleRequestClose() {
    setComposeMail(false)
  }

  function displayMail(currentMail, folderMails) {
    const isNone = currentMail === 'None'
    const filteredMails = folderMails
      ? folderMails
          .filter((mail) =>
            mail?.subject?.toLowerCase()?.includes(search.toLowerCase())
          )
          .filter((mail) =>
            !date
              ? true
              : moment(mail?.date).format('DD/MM/YYYY') ===
                moment(date).format('DD/MM/YYYY')
          )
      : []

    return (
      <div className='gx-module-box-column'>
        {currentMail === null ? (
          !folderMails || (folderMails.length === 0 && !emailsLoading) ? (
            <div className='gx-no-content-found gx-text-light gx-d-flex gx-align-items-center gx-justify-content-center'>
              No Mail found
            </div>
          ) : (
            <MailList
              onMailSelect={onMailSelect}
              mails={filteredMails}
              onLoadMore={loadMoreEmails}
              hasMore={hasMore}
              loadingMore={loadingMore}
            />
          )
        ) : isNone ? (
          <div className='gx-no-content-found gx-text-light gx-d-flex gx-align-items-center gx-justify-content-center'>
            Request timeout, Please try again...
          </div>
        ) : (
          <MailDetail mail={currentMail} />
        )}
      </div>
    )
  }

  function Filters() {
    return (
      <div className='' style={{}}>
        <CustomMeta title='Mail' />
        <div
          className='gx-d-flex gx-align-items-center gx-justify-content-between gx-px-3'
          style={{
            minHeight: 79,
            borderBottom: 'solid 1px #e8e8e8',
            gap: isMobile ? '5px' : '6px',
            flexWrap: 'wrap',
          }}
        >
          <Col className='gx-d-flex gx-px-0' xs={24} sm={12} md={7} lg={7}>
            <Select
              getPopupContainer={(trigger) => trigger.parentElement}
              value={selectedAccount}
              onChange={(value) => {
                setCurrentMail(0)
                setMailList([])
                setOffset(0)
                setHasMore(true)
                setSelectedAccount(value)
              }}
              style={{ width: '100%' }}
            >
              <Option value='UPDATES'>Updates@finideas.com</Option>
              <Option value='GROWTH'>Growth@finideas.com</Option>
              <Option value='ADVISER'>adviser@finideas.com</Option>
            </Select>
          </Col>
          <Col className='gx-d-flex gx-px-0' xs={24} sm={12} md={9} lg={9}>
            <Input
              placeholder='Search Mails'
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              className='gx-pl-2'
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={6}>
            <DatePicker
              style={{ height: '38px', width: '100%' }}
              getPopupContainer={(trigger) => trigger.parentElement}
              format={'DD-MMM-YYYY'}
              onChange={(e) => setDate(e)}
            />
          </Col>
        </div>
      </div>
    )
  }

  async function onMailSend(variables) {
    if (variables.attachment) {
      let attachment = []
      for (const file of variables.attachment) {
        attachment.push(file.originFileObj)
      }
      delete variables.attachment
      variables.attachment = attachment
    }
    if (!_isEmpty(variables.toEmailsAddress)) {
      const tempEmails = [
        variables.toEmailAddress,
        ...variables.toEmailsAddress.map(
          (to, key) => to[`toEmailAddress_${key}`]
        ),
      ]
      variables.toEmailAddress = tempEmails.join(',')
    }
    delete variables.toEmailsAddress
    const payload = {
      account: selectedAccount,
      ...variables,
    }
    sendMail({ variables: payload })
      .then(() => openNotification('success', 'Mail Sent Successfully'))
      .finally(() => setComposeMail(false))
  }


  function onMailSelect(mail) {
    setCurrentMail(mail)
  }

  function onToggleDrawer() {
    setDrawerState(!drawerState)
  }

  return (
    <div
      className='gx-main-content'
      style={{ padding: isMobile ? '14px 20px' : '32px 40px' }}
    >
      <div className='gx-app-module'>
        <div className='gx-d-block gx-d-lg-none'>
          <Drawer
            placement='left'
            closable={false}
            visible={drawerState}
            onClose={onToggleDrawer}
          >
            {MailSideBar()}
          </Drawer>
        </div>
        <div className='gx-module-sidenav gx-d-none gx-d-lg-flex'>
          {MailSideBar()}
        </div>

        <div className='gx-module-box'>
          <div className='gx-module-box-content'>
            {Filters()}
            {(emailsLoading && mailList.length === 0) ||
            (currentMail !== 0 && detailEmailLoading) ? (
              <div className='gx-loader-view'>
                <Spinner />
              </div>
            ) : (
              displayMail(detailEmail, mailList)
            )}
            {composeMail && (
              <ComposeMail
                to={email}
                open={composeMail}
                onMailSend={onMailSend}
                onClose={handleRequestClose}
                composeMailLoading={composeMailLoading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
